import { useEffect, useState } from "react";
import { Card, Col, Row, Button, Typography, Tag, Space } from "antd";
import { DownloadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import NpsUserService from "@/services/FinEaseHomeAndAdmin/APIs/NpsUserService";
import { useParams } from "react-router-dom";
import { Registration } from "@/utils/types/NpsUser";
import LoadingSpinner from "@/common/components/common/LoadingSpinner";

const { Title, Text, Paragraph } = Typography;

const NpsUserDetails = () => {
  const { id } = useParams();
  const [userDetails, setUserDetails] = useState<Registration | undefined>(
    undefined
  );

  useEffect(() => {
    if (id) {
      NpsUserService.getNpsUserById(id)
        .then(response => {
          setUserDetails(response.registration);
        })
        .catch(err => console.error("Error fetching user details:", err));
    }
  }, [id]);

  if (!userDetails) {
    return (
      <div className="p-6">
        <LoadingSpinner fullScreen={false} text="Loading user details..." />
      </div>
    );
  }

  // Helper function to format currency
  const formatCurrency = (amount: string | number | undefined) => {
    if (!amount) {
      return "₹0";
    }
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  // Helper function to format date
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) {
      return "";
    }

    // Handle DDMMYYYY format (e.g., "06111999")
    if (/^\d{8}$/.test(dateStr)) {
      const day = dateStr.slice(0, 2);
      const month = dateStr.slice(2, 4);
      const year = dateStr.slice(4, 8);
      const isoDate = `${year}-${month}-${day}`;
      const date = new Date(isoDate);

      // Validate date (e.g., prevent "99999999" from becoming a valid date)
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }

    // Fallback for other formats (ISO, etc.)
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Extract details
  const personalDtls = userDetails.personalDtls ?? {};
  const nominationList = userDetails.nominationDtls ?? [];
  const initialDetails = userDetails.initialDetails ?? {};
  const bankDtls = userDetails.bankDtls?.[0] ?? {};
  const schemeDtls = userDetails.schemeDtls?.[0] ?? {};

  const balanceLifeCycleSchemes = [
    {
      schemeName: "Equity",
      schemePercentage: "50%",
    },
    {
      schemeName: "Corporate",
      schemePercentage: "30%",
    },
    {
      schemeName: "Government",
      schemePercentage: "20%",
    },
  ];

  const AggressiveAutoChoiceSchemes = [
    {
      schemeName: "Equity",
      schemePercentage: "75%",
    },
    {
      schemeName: "Corporate",
      schemePercentage: "10%",
    },
    {
      schemeName: "Government",
      schemePercentage: "15%",
    },
  ];

  const ConservativeAutoChoiceSchemes = [
    {
      schemeName: "Equity",
      schemePercentage: "25%",
    },
    {
      schemeName: "Corporate",
      schemePercentage: "45%",
    },
    {
      schemeName: "Government",
      schemePercentage: "30%",
    },
  ];

  const ModerateAutoChoiceSchemes = [
    {
      schemeName: "Equity",
      schemePercentage: "50%",
    },
    {
      schemeName: "Corporate",
      schemePercentage: "30%",
    },
    {
      schemeName: "Government",
      schemePercentage: "20%",
    },
  ];

  // Mock corpus and returns (replace with real data later)
  const corpus = personalDtls.pran_total_contribution
    ? parseFloat(personalDtls.pran_total_contribution)
    : 685000;
  const totalGains = 105000;
  const returnsPercentage = (
    (totalGains / (corpus - totalGains)) *
    100
  ).toFixed(1);

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      {/* Top Navigation Bar */}
      <div className="mb-5 flex items-center justify-between">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => window.history.back()}
          className="border-none bg-transparent p-0 text-gray-700 hover:text-gray-900"
        >
          Back to List
        </Button>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => console.log("Export")}
          >
            Export
          </Button>
        </Space>
      </div>

      {/* Header Banner */}
      <div className="flex items-center justify-between rounded-t-lg border border-gray-200 bg-white bg-gradient-to-r from-primary/5 to-transparent p-5 shadow-sm">
        {/* Left: User Info */}
        <Space align="center">
          <div>
            {personalDtls?.pPhotoText ? (
              <img
                src={`data:image/png;base64,${personalDtls.pPhotoText}`}
                alt="User"
                className="flex h-[100px] w-[100px] items-center justify-center rounded-full border-2 border-primary/20 bg-primary/10"
                onError={e => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling?.classList.remove(
                    "hidden"
                  );
                }}
              />
            ) : (
              <img
                src="/assets/image.png"
                alt="User"
                className="flex h-[100px] w-[100px] items-center justify-center rounded-full border-2 border-primary/20 bg-primary/10"
                onError={e => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling?.classList.remove(
                    "hidden"
                  );
                }}
              />
            )}
          </div>
          <div>
            <Title level={4} className="!m-0">
              {personalDtls.subFstName || "Unknown User"}
            </Title>
            <Text type="secondary">
              Application ID: {initialDetails.pranNumber || "NPS-00001"}
            </Text>
            <div className="mt-1">
              <Tag color="green" className="rounded-full px-2">
                Active
              </Tag>
              <Tag color="green" className="rounded-full px-2">
                Verified
              </Tag>
            </div>
          </div>
        </Space>

        {/* Right: Corpus & Returns */}
        <div className="flex gap-3">
          <div className="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-center">
            <Text strong className="block text-xs">
              Corpus
            </Text>
            <Text className="block text-lg font-semibold text-blue-700">
              {formatCurrency(corpus)}
            </Text>
          </div>
          <div className="rounded-md border border-green-300 bg-green-50 px-4 py-2 text-center">
            <Text strong className="block text-xs">
              Returns
            </Text>
            <Text className="block text-lg font-semibold text-green-600">
              +{returnsPercentage}%
            </Text>
          </div>
        </div>
      </div>

      {/* Personal Details */}
      <Card
        title={
          <Space align="center">
            <UserIcon />
            <span>Personal Details</span>
          </Space>
        }
        className="rounded-none"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Full Name</Text>
            <Paragraph>{personalDtls.subFstName || "Not Available"}</Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Date of Birth</Text>
            <Paragraph>
              {formatDate(personalDtls.subDob) || "Not Available"}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Gender</Text>
            <Paragraph>{personalDtls.gender || "Not Available"}</Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Email Address</Text>
            <Paragraph>{personalDtls.email || "Not Available"}</Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Mobile Number</Text>
            <Paragraph>{personalDtls.mobile || "Not Available"}</Paragraph>
          </Col>
        </Row>
      </Card>

      {/* Address Details */}
      <Card
        title={
          <Space align="center">
            <LocationIcon />
            <span>Address Details</span>
          </Space>
        }
        className="rounded-none"
      >
        <Row>
          <Col span={24}>
            <Text strong>Residential Address</Text>
            <Paragraph>
              {personalDtls.pAddrLine1 +
                " " +
                personalDtls.pAddrLine2 +
                " " +
                personalDtls.pAddrLine3 +
                " " +
                personalDtls.pAddrLine4 || "Not Available"}
            </Paragraph>
          </Col>
        </Row>
      </Card>

      {/* KYC Documents */}
      <Card
        title={
          <Space align="center">
            <DocumentIcon />
            <span>KYC Documents</span>
          </Space>
        }
        extra={<Tag color="green">Verified</Tag>}
        className="rounded-none"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Text strong>PAN Number</Text>
            <Paragraph>{personalDtls.pan || "Not Available"}</Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Aadhaar Number</Text>
            <Paragraph>
              {personalDtls.pAadhaarLast4
                ? `XXXX XXXX ${personalDtls.pAadhaarLast4}`
                : "Not Available"}
            </Paragraph>
          </Col>
        </Row>
      </Card>

      {/* Bank Details */}
      <Card
        title={
          <Space align="center">
            <BankIcon />
            <span>Bank Details</span>
          </Space>
        }
        className="rounded-none"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Bank Account Type</Text>
            <Paragraph>{bankDtls.accountType || "Savings"}</Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Bank Name</Text>
            <Paragraph>{bankDtls.bankName || "HDFC Bank"}</Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Account Number</Text>
            <Paragraph>
              {bankDtls.bankAccountNumber || "XXXX XXXX 5678"}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>IFSC Code</Text>
            <Paragraph>{bankDtls.bankIfsCode || "HDFC0001234"}</Paragraph>
          </Col>
        </Row>
      </Card>

      {/* Investment Preferences */}
      <Card
        title={
          <Space align="center">
            <InvestmentIcon />
            <span>Investment Preferences</span>
          </Space>
        }
        className="rounded-none"
      >
        <Row gutter={[16, 16]}>
          {/* PRAN Number */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>PRAN Number</Text>
            <Paragraph className="rounded bg-blue-50 px-2 py-1">
              {initialDetails.pranNumber || "1100345678901"}
            </Paragraph>
          </Col>

          {/* Account Tier */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>Account Tier</Text>
            <Paragraph>
              <Tag color="blue">{schemeDtls.tier || "Tier 1 & 2"}</Tag>
            </Paragraph>
          </Col>

          {/* Risk Profile */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>Risk Profile</Text>
            <Paragraph>
              <Tag color="red">High</Tag>
            </Paragraph>
          </Col>

          {/* Pension Fund Manager */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>Pension Fund Manager</Text>
            <Paragraph>
              {schemeDtls.schemeList?.[0]?.pfmName || "Not Available"}
            </Paragraph>
          </Col>

          {/* Contribution Frequency */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>Contribution Frequency</Text>
            <Paragraph>Monthly</Paragraph>
          </Col>

          {/* Contribution Amount */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>Contribution Amount</Text>
            <Paragraph>
              {formatCurrency(personalDtls.pran_total_contribution) ||
                "₹10,000"}
            </Paragraph>
          </Col>

          {/* Investment Choice */}
          <Col xs={24} sm={12} md={6}>
            <Text strong>Investment Choice</Text>
            <Paragraph>{schemeDtls.schPrefChoice || "Not Available"}</Paragraph>
          </Col>
        </Row>

        {/* Balance Life Cycle */}
        {schemeDtls.schPrefChoice === "Balance Life Cycle" &&
          schemeDtls.schemeList?.length > 0 && (
            <div className="mt-6">
              <Title level={5} className="!mb-3">
                Investment Allocation
              </Title>
              <div className="space-y-3">
                {balanceLifeCycleSchemes.map((scheme: any) => (
                  <div
                    key={scheme.schemeName}
                    className="flex flex-wrap items-center gap-4 rounded border bg-gray-50 p-3"
                  >
                    <div>
                      <Text type="secondary">Scheme</Text>
                      <Paragraph className="!mb-0 font-medium">
                        {scheme.schemeName}
                      </Paragraph>
                    </div>
                    <div>
                      <Text type="secondary">Allocation</Text>
                      <Paragraph className="!mb-0 font-semibold">
                        {scheme.schemePercentage}
                      </Paragraph>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Aggressive Auto Choice */}
        {schemeDtls.schPrefChoice === "Aggressive Auto Choice" &&
          schemeDtls.schemeList?.length > 0 && (
            <div className="mt-6">
              <Title level={5} className="!mb-3">
                Investment Allocation
              </Title>
              <div className="space-y-3">
                {AggressiveAutoChoiceSchemes.map((scheme: any) => (
                  <div
                    key={scheme.schemeName}
                    className="flex flex-wrap items-center gap-4 rounded border bg-gray-50 p-3"
                  >
                    <div>
                      <Text type="secondary">Scheme</Text>
                      <Paragraph className="!mb-0 font-medium">
                        {scheme.schemeName}
                      </Paragraph>
                    </div>
                    <div>
                      <Text type="secondary">Allocation</Text>
                      <Paragraph className="!mb-0 font-semibold">
                        {scheme.schemePercentage}
                      </Paragraph>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Conservative Auto Choice */}
        {schemeDtls.schPrefChoice === "Conservative Auto Choice" &&
          schemeDtls.schemeList?.length > 0 && (
            <div className="mt-6">
              <Title level={5} className="!mb-3">
                Investment Allocation
              </Title>
              <div className="space-y-3">
                {ConservativeAutoChoiceSchemes.map((scheme: any) => (
                  <div
                    key={scheme.schemeName}
                    className="flex flex-wrap items-center gap-4 rounded border bg-gray-50 p-3"
                  >
                    <div>
                      <Text type="secondary">Scheme</Text>
                      <Paragraph className="!mb-0 font-medium">
                        {scheme.schemeName}
                      </Paragraph>
                    </div>
                    <div>
                      <Text type="secondary">Allocation</Text>
                      <Paragraph className="!mb-0 font-semibold">
                        {scheme.schemePercentage}
                      </Paragraph>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Moderate Auto Choice */}
        {schemeDtls.schPrefChoice === "Moderate Auto Choice" &&
          schemeDtls.schemeList?.length > 0 && (
            <div className="mt-6">
              <Title level={5} className="!mb-3">
                Investment Allocation
              </Title>
              <div className="space-y-3">
                {ModerateAutoChoiceSchemes.map((scheme: any) => (
                  <div
                    key={scheme.schemeName}
                    className="flex flex-wrap items-center gap-4 rounded border bg-gray-50 p-3"
                  >
                    <div>
                      <Text type="secondary">Scheme</Text>
                      <Paragraph className="!mb-0 font-medium">
                        {scheme.schemeName}
                      </Paragraph>
                    </div>
                    <div>
                      <Text type="secondary">Allocation</Text>
                      <Paragraph className="!mb-0 font-semibold">
                        {scheme.schemePercentage}
                      </Paragraph>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Active Choice: Show Scheme List */}
        {schemeDtls.schPrefChoice === "Active Choice" &&
          schemeDtls.schemeList?.length > 0 && (
            <div className="mt-6">
              <Title level={5} className="!mb-3">
                Investment Allocation
              </Title>
              <div className="space-y-3">
                {schemeDtls.schemeList.map((scheme: any) => (
                  <div
                    key={scheme.schemeName}
                    className="flex flex-wrap items-center gap-4 rounded border bg-gray-50 p-3"
                  >
                    <div>
                      <Text type="secondary">Scheme</Text>
                      <Paragraph className="!mb-0 font-medium">
                        {scheme.schemeName ?? "N/A"}
                      </Paragraph>
                    </div>

                    <div>
                      <Text type="secondary">Allocation</Text>
                      <Paragraph className="!mb-0 font-semibold">
                        {scheme.schemePercentage ?? "N/A"}%
                      </Paragraph>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </Card>

      {/* Nominee Details */}
      <Card
        title={
          <Space align="center">
            <UserIcon />
            <span>Nominee Details</span>
          </Space>
        }
        className="rounded-none"
      >
        {nominationList.length > 0 ? (
          nominationList.map(nominee => (
            <div
              key={nominee.firstName + nominee.lastName}
              className="mb-4 border-b border-gray-100 pb-4 last:mb-0 last:border-b-0 last:pb-0"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Nominee Name</Text>
                  <Paragraph>
                    {(nominee.firstName ?? "") + " " + (nominee.lastName ?? "")}
                  </Paragraph>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Relationship</Text>
                  <Paragraph>
                    {nominee.relationship ?? "Not Available"}
                  </Paragraph>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Major/Minor</Text>
                  <Paragraph>{nominee.majorMinorFlag ?? "Major"}</Paragraph>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Percentage Share</Text>
                  <Paragraph>{nominee.percentageShare ?? "0%"}</Paragraph>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Nominee DOB</Text>
                  <Paragraph>
                    {formatDate(nominee.dateOfBirth) ?? "Not Available"}
                  </Paragraph>
                </Col>
              </Row>
              {/* {nominationList.length > 1 && index < nominationList.length - 1 && (
          <Divider className="my-4" />
        )} */}
            </div>
          ))
        ) : (
          <Paragraph>No nominee details available.</Paragraph>
        )}
      </Card>
      {/* Account Summary */}
      <Card
        title={
          <Space align="center">
            <ChartIcon />
            <span>Account Summary</span>
          </Space>
        }
        className="rounded-t-none"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Member Since</Text>
            <Paragraph>
              {formatDate(userDetails.created_on) ?? "15 Jan 2020"}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Total Contributed</Text>
            <Paragraph className="text-gray-900">
              {formatCurrency(personalDtls.pran_total_contribution) ??
                "₹5,80,000"}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Current Corpus</Text>
            <Paragraph className="text-green-600">
              {formatCurrency(corpus) ?? "₹6,85,000"}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong>Total Gains</Text>
            <Paragraph className="text-cyan-600">
              {formatCurrency(totalGains) ?? "₹1,05,000"}
            </Paragraph>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// --- SVG Icons as Components (for cleaner JSX) ---
const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 2 15.33 2 18V20H22V18C22 15.33 14.67 14 12 14Z"
      fill="#52c41a"
    />
  </svg>
);

const LocationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
      fill="#52c41a"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z"
      fill="#52c41a"
    />
  </svg>
);

const BankIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"
      fill="#52c41a"
    />
  </svg>
);

const InvestmentIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 20H7v-2h10v2zM18 4h3v2h-3v6h-2V6H5V4h3V2h2v2h4V2h2v2z"
      fill="#52c41a"
    />
  </svg>
);

const ChartIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 5H5v14h14V5zM9 17H7v-7h2v7zm6 0h-2v-4h2v4zm-6-9H7V6h2v2zm6 0h-2V6h2v2z"
      fill="#52c41a"
    />
  </svg>
);

export default NpsUserDetails;
